import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { SystemMessage } from '../../system-message/system-message-service';
import { ActivatedRoute, Router } from '@angular/router';

import { SecurityService } from 'app/core/security/security.service';
import { LogoutService } from 'app/core/security/logout.service';
import { MobileScanningService } from './mobile-scanning.service';
import { ScannerComponent } from './scanner/scanner.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faVolume, faVolumeSlash } from '@fortawesome/pro-solid-svg-icons';
import { StrichService } from 'app/shared/services/strich.service';

@Component({
    selector: 'pcg-mobile-scanning',
    templateUrl: './mobile-scanning.component.html',
    styleUrls: ['./mobile-scanning.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ ScannerComponent, FontAwesomeModule ]
})
export class MobileScanningComponent {

    http = inject(HttpClient);
    toastr = inject(ToastrService);
    sec = inject(SecurityService);
    logoutService = inject(LogoutService);
    router = inject(Router);
    route = inject(ActivatedRoute);
    scannerService = inject(MobileScanningService);
    strichService = inject(StrichService)

    fa = { faVolume: faVolume, faVolumeSlash: faVolumeSlash };
    
    detectedSubscription?: Subscription; 
    scanCount = signal<number>(0);
    recentScan = signal<boolean>(false);
    highlightTimeout: ReturnType<typeof setTimeout> | null = null;

    ngOnInit() {
        this.detectScanner();
    }

    ngAfterViewInit() {
        const qp = this.route.snapshot.queryParamMap;
        const isPidStr = qp.get('isPid') ?? '';
        const isPid = isPidStr === 'true';

        this.scannerService.isPid.set(isPid);
    }

    ngOnDestroy() { 
        this.detectedSubscription?.unsubscribe();
        this.scannerService.releaseBarcodeReader();  
    }

    // Detects the scanner and subscribes to the detected event.
    detectScanner() {
        this.detectedSubscription = this.scannerService.detected
            .subscribe((detections) => {  
                try {
                    this.scanCount.update(count => count + 1);
                    this.highlightScanCount();
                    this.processScan(detections[0].data);
                } catch (ex) {
                    this.toastr.error(`There was an error loading the scanner: ${ex}`, null, { positionClass: 'toast-top-right', timeOut: 2000});
                }
            }
        );
    }

    // Briefly highlights the scan count when a new scan is detected.
    highlightScanCount() {
        // Clear any existing timeout to prevent multiple pending timeouts
        if (this.highlightTimeout) {
            clearTimeout(this.highlightTimeout);
        }
        
        this.recentScan.set(true);
        this.highlightTimeout = setTimeout(() => {
            this.recentScan.set(false);
            this.highlightTimeout = null;
        }, 300);
    }

    // Processes the scanned value by sending it to the server.
    processScan(value: string) {
        this.http.post<SystemMessage>(`api/MobileScanning/Scan?parentUrl=${encodeURIComponent(this.sec.parentUrl())}`, { value })
        .pipe(
            tap((response: SystemMessage) => {
            if(!response.isSuccessful) {
                this.toastr.error(response.message, null, { positionClass: 'toast-top-right', timeOut: 2000});
            }
            })
        ).subscribe()
    }

    // Ends the scanning session, logs user out and redirects to the login page.
    finishScanning() {
        this.logoutService.logout(this.sec.getUser().id);
        this.router.navigate(['/login']);
    }

    // Toggles the use of audio feedback for scans.
    toggleUseAudio() { 
        this.scannerService.useAudio.update(value => !value);
    }

    // Example API call to non whitelisted api. The MobileScanningInterceptor should 
    // block this call if sec.isMobileScanningMode = true
    testApiCall() {
        // This is just an example, replace with any endpoint you want to test
        let pkuOrderId = 30; // Example PkuOrderId
        this.http.get(`api/MetabolicFormula/MetabolicFormula/GetOrder/${pkuOrderId}`)
            .subscribe((resp: any) => {
                this.toastr.success('Oh no! API call successful :(', null, { positionClass: 'toast-top-right', timeOut: 2000 });
            }
        );
    }
}